import { type Action, createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { containsBlog } from 'src/data/blog';
import { BRIEF_TYPE, mapExtractedContentForEditing } from 'src/data/brief';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { type AllInstructionsData, EMPTY_ALL_INSTRUCTIONS_DATA } from 'src/data/generation';
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import { CLASSIFIED_TEMPLATE_BLOG, EDITOR_TYPE, type EditorType } from 'src/data/project';
import { type GenericOrSubTemplate, type TemplateClassifyResult } from 'src/data/template';
// @ts-expect-error - brief graphql doesn't have TypeScript definitions
import { extractBrief } from 'src/graphql/brief';
// @ts-expect-error - longFormBuilder graphql doesn't have TypeScript definitions
import { classifyLongForm } from 'src/graphql/longFormBuilder';
import { takeFulfilledResults } from 'src/lib/async';
import * as ls from 'src/lib/localData';
import { EMPTY_PARAGRAPH_HTML } from 'src/lib/slate';
import { extractUrls } from 'src/lib/url';
// @ts-expect-error - prepareTemplateClassifier doesn't have TypeScript definitions
import { prepareTemplateClassifier } from 'src/services/templates/prepareTemplateClassifier';
// @ts-expect-error - waitForBriefExtraction doesn't have TypeScript definitions
import { waitForBriefExtraction } from 'src/services/waitForBriefExtraction';

import { type ContainerProps } from './MainStoreContainer';

interface UrlExtraction {
  url: string;
  extractedBrief: string;
}

export type State = {
  promptText: string;
  promptData: AllInstructionsData;
  classifyingTemplate: boolean;
  editorType: EditorType;
};

export type Actions = typeof ACTIONS;

const INITIAL_STATE: State = {
  promptText: EMPTY_PARAGRAPH_HTML,
  promptData: EMPTY_ALL_INSTRUCTIONS_DATA,
  classifyingTemplate: false,
  editorType: EDITOR_TYPE.longForm,
};

const alignLongFormTemplate = (template: string): GenerationTool => {
  if (template === CLASSIFIED_TEMPLATE_BLOG) {
    return GENERATION_TOOL.blogDescription;
  }

  return GENERATION_TOOL.longFormBlank;
};

export const PRIVATE_ACTIONS = {
  initStore:
    (): Action<State> =>
    ({ setState }) => {
      setState({
        ...cloneDeep(INITIAL_STATE),
        editorType: ls.selectedEditorType.read(EDITOR_TYPE.longForm) || INITIAL_STATE.editorType,
      });
    },

  extractBriefs:
    (urlsToExtract: string[]): Action<State, ContainerProps, Promise<UrlExtraction[]>> =>
    async (_, { currentCustomerId }) => {
      if (!urlsToExtract || urlsToExtract.length === 0) {
        return Promise.resolve([]);
      }

      const promises = urlsToExtract.map(async (url) => {
        const extractionJob = await extractBrief({
          customerId: currentCustomerId,
          url,
          briefType: BRIEF_TYPE.unifiedContent, // for url import, brief type should always be unifiedContent
        });
        const extractedBrief = await waitForBriefExtraction(extractionJob.id);

        const newBrief = mapExtractedContentForEditing(extractedBrief);
        const body = newBrief?.article || '';
        return {
          url,
          extractedBrief: body,
        };
      });

      const results = await Promise.allSettled(promises);

      return takeFulfilledResults(results) as UrlExtraction[];
    },

  prepareUrlExtractions:
    (): Action<State, ContainerProps, Promise<UrlExtraction[]>> =>
    async ({ getState, dispatch }) => {
      const { promptData } = getState();

      // Get URLs from prompt data
      const content = promptData?.text || '';
      const urls = extractUrls(content, MAX_RESOURCE_PER_TYPE)?.urls || [];

      // Wait for briefs extraction
      const result = await dispatch(PRIVATE_ACTIONS.extractBriefs(urls));
      return result || [];
    },
};

/**
 * Publicly available actions
 */
export const ACTIONS = {
  getPromptText:
    (): Action<State, void, State['promptText']> =>
    ({ getState }) => {
      return getState().promptText;
    },

  getPromptData:
    (): Action<State, void, State['promptData']> =>
    ({ getState }) => {
      return getState().promptData;
    },

  updatePrompt:
    (text: string, data: AllInstructionsData): Action<State> =>
    ({ setState }) => {
      setState({
        promptText: text,
        promptData: data,
      });
    },

  updateEditorType:
    (editorType: EditorType): Action<State> =>
    ({ setState }) => {
      ls.selectedEditorType.write(editorType);
      setState({ editorType });
    },

  classifyTemplate:
    (
      templates: GenericOrSubTemplate[]
    ): Action<State, ContainerProps, Promise<TemplateClassifyResult>> =>
    async ({ getState, setState, dispatch }, { currentCustomerId }) => {
      const { promptData, editorType } = getState();

      setState({ classifyingTemplate: true });
      try {
        const blogFound = containsBlog(promptData?.text || '');
        if (blogFound) {
          const urlExtractions = await dispatch(PRIVATE_ACTIONS.prepareUrlExtractions());
          const longFormTemplate = await classifyLongForm({
            customerId: currentCustomerId,
            allInstructionsData: promptData,
            urlExtractions,
          });
          return { generationTool: alignLongFormTemplate(longFormTemplate?.template) };
        }
        // classify template
        const urlExtractions = await dispatch(PRIVATE_ACTIONS.prepareUrlExtractions());
        const classifyTemplate = prepareTemplateClassifier({
          templates,
          customerId: currentCustomerId,
          editorType,
        });
        return await classifyTemplate({
          allInstructionsData: promptData,
          urlExtractions,
        });
      } finally {
        setState({ classifyingTemplate: false });
      }
    },
};

export const MainStore = createStore<State, Actions, ContainerProps>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'new-project-prompt-widget-main',
});
